// client/src/components/pages/LexiStar/DispatchBoard/OrderDialog.tsx (ST-00250)
//
// Add or change an order bubble: order reference, customer, material, lane (+ route when the lane
// has several), target tonnes and / or loads, the planning tonnes per load, the first load's start
// (SAST, 15-minute steps), the shift, notes — and, when editing, the status. The version travels
// with a change (the server refuses a stale one; the page refreshes and says so).

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { FaTimes } from 'react-icons/fa';
import { ThemedSelect } from '../../../../types/commonComponents';
import type { BoardOrder, DispatchShift, OrderInput, OrderPatch, OrderStatus } from '../../../../types/lexistar-dispatch-board';
import type { PlanLane, PlanRoute } from '../PlanDispatch/planTypes';
import { inputClasses } from '../StatusBoard/atoms';

export interface OrderDialogProps {
  isOpen: boolean;
  /** Editing this order, or null for a new one. */
  order: BoardOrder | null;
  date: string;
  defaultShift: DispatchShift | null;
  lanes: PlanLane[];
  routes: PlanRoute[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (input: OrderInput) => Promise<unknown>;
  onUpdate: (id: string, patch: OrderPatch) => Promise<unknown>;
}

const TIMES: string[] = [];
for (let h = 0; h < 24; h++) for (const m of ['00', '15', '30', '45']) TIMES.push(`${String(h).padStart(2, '0')}:${m}`);

const laneLabel = (l: PlanLane): string =>
  l.name || `${l.from_location_name || l.from_name || l.from_location_id} → ${l.to_location_name || l.to_name || l.to_location_id}`;

interface Form {
  order_ref: string;
  customer: string;
  material: string;
  lane_id: string;
  route_id: string;
  target_tonnes: string;
  target_loads: string;
  tonnes_per_load: string;
  start_time: string;
  shift: '' | DispatchShift;
  notes: string;
  status: OrderStatus;
}

const emptyForm = (shift: DispatchShift | null): Form => ({
  order_ref: '',
  customer: '',
  material: '',
  lane_id: '',
  route_id: '',
  target_tonnes: '',
  target_loads: '',
  tonnes_per_load: '',
  start_time: '06:00',
  shift: shift ?? '',
  notes: '',
  status: 'open',
});

const formOf = (o: BoardOrder): Form => ({
  order_ref: o.order_ref,
  customer: o.customer ?? '',
  material: o.material ?? '',
  lane_id: o.lane_id,
  route_id: o.route_id ?? '',
  target_tonnes: o.target_tonnes == null ? '' : String(o.target_tonnes),
  target_loads: o.target_loads == null ? '' : String(o.target_loads),
  tonnes_per_load: o.tonnes_per_load == null ? '' : String(o.tonnes_per_load),
  start_time: o.start_time,
  shift: o.shift ?? '',
  notes: o.notes ?? '',
  status: o.status,
});

const num = (s: string): number | null => (s.trim() === '' ? null : Number(s));

export function validateForm(f: Form): string[] {
  const errors: string[] = [];
  if (!f.order_ref.trim()) errors.push('Give the order reference.');
  if (!f.lane_id) errors.push('Pick the lane.');
  const t = num(f.target_tonnes);
  const l = num(f.target_loads);
  if (t == null && l == null) errors.push('Give a target in tonnes or in loads (or both).');
  if (t != null && !(t > 0)) errors.push('Target tonnes must be more than 0.');
  if (l != null && !(Number.isInteger(l) && l > 0)) errors.push('Target loads must be a whole number above 0.');
  const p = num(f.tonnes_per_load);
  if (p != null && !(p > 0)) errors.push('Tonnes per load must be more than 0.');
  return errors;
}

export default function OrderDialog({ isOpen, order, date, defaultShift, lanes, routes, saving, error, onClose, onCreate, onUpdate }: OrderDialogProps) {
  const [form, setForm] = useState<Form>(() => emptyForm(defaultShift));
  const [problems, setProblems] = useState<string[]>([]);
  useEffect(() => {
    if (isOpen) {
      setForm(order ? formOf(order) : emptyForm(defaultShift));
      setProblems([]);
    }
  }, [isOpen, order, defaultShift]);

  const laneRoutes = useMemo(() => routes.filter((r) => r.lane_id === form.lane_id && r.status !== 'retired'), [routes, form.lane_id]);
  if (!isOpen) return null;

  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validateForm(form);
    setProblems(errs);
    if (errs.length) return;
    const common = {
      order_ref: form.order_ref.trim(),
      customer: form.customer.trim() || null,
      material: form.material.trim() || null,
      route_id: form.route_id || null,
      target_tonnes: num(form.target_tonnes),
      target_loads: num(form.target_loads),
      tonnes_per_load: num(form.tonnes_per_load),
      start_time: form.start_time,
      shift: form.shift || null,
      notes: form.notes.trim() || null,
    };
    if (order) {
      await onUpdate(order.id, { version: order.version, ...common, status: form.status });
    } else {
      await onCreate({ plan_date: date, lane_id: form.lane_id, ...common });
    }
  };

  const field = 'block text-xs font-medium text-slate-600 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="dispatch-order-title"
        onSubmit={submit}
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-auto"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 id="dispatch-order-title" className="text-lg font-bold text-slate-900">
            {order ? `Order ${order.order_ref}` : `New order for ${date}`}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600">
            <FaTimes size={18} />
          </button>
        </div>
        <div className="p-6 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="do-ref" className={field}>
              Order reference *
            </label>
            <input id="do-ref" value={form.order_ref} onChange={(e) => set({ order_ref: e.target.value })} className={inputClasses} placeholder="PO-4477" maxLength={120} />
          </div>
          <div>
            <label htmlFor="do-customer" className={field}>
              Customer
            </label>
            <input id="do-customer" value={form.customer} onChange={(e) => set({ customer: e.target.value })} className={inputClasses} maxLength={200} />
          </div>
          <div>
            <label htmlFor="do-material" className={field}>
              Material
            </label>
            <input id="do-material" value={form.material} onChange={(e) => set({ material: e.target.value })} className={inputClasses} maxLength={120} />
          </div>
          <div>
            <label htmlFor="do-lane" className={field}>
              Lane *
            </label>
            <ThemedSelect
              id="do-lane"
              value={form.lane_id}
              onChange={(e) => set({ lane_id: e.target.value, route_id: '' })}
              disabled={!!order}
              placeholder="Pick the lane"
              aria-label="Lane"
            >
              <option value="">Pick the lane</option>
              {lanes.map((l) => (
                <option key={l.id} value={l.id}>
                  {laneLabel(l)}
                </option>
              ))}
            </ThemedSelect>
            {order && <p className="mt-1 text-[11px] text-slate-500">The lane cannot change once the order is on the board; add a new order instead.</p>}
          </div>
          {laneRoutes.length > 0 && (
            <div>
              <label htmlFor="do-route" className={field}>
                Route
              </label>
              <ThemedSelect id="do-route" value={form.route_id} onChange={(e) => set({ route_id: e.target.value })} aria-label="Route" isClearable>
                <option value="">Any approved route of the lane</option>
                {laneRoutes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code}
                    {r.name ? ` — ${r.name}` : ''}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          )}
          <div>
            <label htmlFor="do-tonnes" className={field}>
              Target tonnes
            </label>
            <input id="do-tonnes" type="number" inputMode="decimal" min={0} step="any" value={form.target_tonnes} onChange={(e) => set({ target_tonnes: e.target.value })} className={inputClasses} placeholder="600" />
          </div>
          <div>
            <label htmlFor="do-loads" className={field}>
              Target loads
            </label>
            <input id="do-loads" type="number" inputMode="numeric" min={0} step={1} value={form.target_loads} onChange={(e) => set({ target_loads: e.target.value })} className={inputClasses} placeholder="18" />
          </div>
          <div>
            <label htmlFor="do-per-load" className={field}>
              Planning tonnes per load
            </label>
            <input id="do-per-load" type="number" inputMode="decimal" min={0} step="any" value={form.tonnes_per_load} onChange={(e) => set({ tonnes_per_load: e.target.value })} className={inputClasses} placeholder="34" />
            <p className="mt-1 text-[11px] text-slate-500">Used until a truck's own 90-day average takes over.</p>
          </div>
          <div>
            <label htmlFor="do-start" className={field}>
              First load starts (SAST)
            </label>
            <ThemedSelect id="do-start" value={form.start_time} onChange={(e) => set({ start_time: e.target.value })} aria-label="First load starts">
              {TIMES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </ThemedSelect>
          </div>
          <div>
            <label htmlFor="do-shift" className={field}>
              Shift
            </label>
            <ThemedSelect id="do-shift" value={form.shift} onChange={(e) => set({ shift: e.target.value as Form['shift'] })} aria-label="Shift">
              <option value="">Whole day</option>
              <option value="day">Day</option>
              <option value="night">Night</option>
            </ThemedSelect>
          </div>
          {order && (
            <div>
              <label htmlFor="do-status" className={field}>
                Status
              </label>
              <ThemedSelect id="do-status" value={form.status} onChange={(e) => set({ status: e.target.value as OrderStatus })} aria-label="Status">
                <option value="open">Open</option>
                <option value="closed">Closed (no more trucks)</option>
                <option value="cancelled">Cancelled (off the board)</option>
              </ThemedSelect>
            </div>
          )}
          <div className="sm:col-span-2">
            <label htmlFor="do-notes" className={field}>
              Notes
            </label>
            <textarea id="do-notes" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} className={inputClasses} maxLength={2000} />
          </div>
          {(problems.length > 0 || error) && (
            <ul className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 list-disc list-inside" role="alert">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
              {error && <li>{error}</li>}
            </ul>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400">
            {saving ? 'Saving…' : order ? 'Save changes' : 'Add to board'}
          </button>
        </div>
      </form>
    </div>
  );
}
